// useOrgViewFilters — URL-param-synced state for the Org View page (T-049,
// plan §6.1). The URL is the canonical source: `?search=&teamId=&siteId=&status=`.
//
// Why URL-driven?
//   - Deep links work (paste a filter URL into Slack, the receiver sees
//     the same filtered grid).
//   - Browser back/forward restore the filter state without extra wiring.
//   - The page can be reloaded without losing the filter selection.
//
// Defaults (per spec §5.x and the server contract from T-047):
//   - search    → ''      (empty = no filter)
//   - teamId    → 'all'
//   - siteId    → 'all'
//   - status    → 'active'
//
// Each setter updates the URL via `useSearchParams` and *omits* the
// param when the value is the default (keeps URLs short).

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OrgViewFilters } from '@pa/shared';

export const ORG_VIEW_DEFAULTS: OrgViewFilters = {
  search: '',
  teamId: 'all',
  siteId: 'all',
  status: 'active',
};

const VALID_STATUS = new Set(['active', 'inactive', 'all']);

export interface UseOrgViewFiltersResult {
  filters: OrgViewFilters;
  setSearch: (next: string) => void;
  setTeamId: (next: string) => void;
  setSiteId: (next: string) => void;
  setStatus: (next: 'active' | 'inactive' | 'all') => void;
  resetAll: () => void;
}

export function useOrgViewFilters(): UseOrgViewFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<OrgViewFilters>(() => {
    const rawStatus = searchParams.get('status');
    return {
      search: searchParams.get('search') ?? ORG_VIEW_DEFAULTS.search,
      teamId: searchParams.get('teamId') ?? ORG_VIEW_DEFAULTS.teamId,
      siteId: searchParams.get('siteId') ?? ORG_VIEW_DEFAULTS.siteId,
      status:
        rawStatus && VALID_STATUS.has(rawStatus)
          ? (rawStatus as 'active' | 'inactive' | 'all')
          : ORG_VIEW_DEFAULTS.status,
    };
  }, [searchParams]);

  const update = useCallback(
    (
      key: keyof OrgViewFilters,
      value: string,
      defaultValue: string,
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value || value === defaultValue) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (next: string) => update('search', next, ORG_VIEW_DEFAULTS.search),
    [update],
  );
  const setTeamId = useCallback(
    (next: string) => update('teamId', next, ORG_VIEW_DEFAULTS.teamId),
    [update],
  );
  const setSiteId = useCallback(
    (next: string) => update('siteId', next, ORG_VIEW_DEFAULTS.siteId),
    [update],
  );
  const setStatus = useCallback(
    (next: 'active' | 'inactive' | 'all') =>
      update('status', next, ORG_VIEW_DEFAULTS.status),
    [update],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('search');
        next.delete('teamId');
        next.delete('siteId');
        next.delete('status');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return { filters, setSearch, setTeamId, setSiteId, setStatus, resetAll };
}
